import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Tab, 
  Tabs,
  Link as MuiLink,
  Paper,
  useTheme as useMuiTheme 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import CodeIcon from '@mui/icons-material/Code';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import TerminalIcon from '@mui/icons-material/Terminal';
import MemoryIcon from '@mui/icons-material/Memory';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import LinearProgress from '@mui/material/LinearProgress';
import { SiFlutter, SiArduino, SiPython, SiCplusplus, SiRaspberrypi, SiDart, SiReact, SiTensorflow, SiFirebase } from 'react-icons/si';
import { FaJava, FaCode, FaRobot, FaMicrochip } from 'react-icons/fa';

// Styled components
const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  '& .MuiTab-root': {
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'auto',
    padding: '10px 16px',
    marginRight: theme.spacing(2),
    color: theme.palette.text.secondary,
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    opacity: 0.6,
    cursor: 'pointer !important',
    '&:hover': {
      opacity: 0.8,
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(0, 0, 0, 0.04)' 
        : 'rgba(255, 255, 255, 0.04)'
    },
    '&.Mui-selected': {
      opacity: 1,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(106, 73, 219, 0.1)' 
        : 'rgba(106, 73, 219, 0.2)'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      padding: '8px 12px',
      marginRight: theme.spacing(1),
    },
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 500,
  textTransform: 'none',
  minWidth: 120,
  cursor: 'pointer !important',
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const StyledImage = styled(motion.img)(({ theme }) => ({
  width: '100%',
  height: 'auto',
  borderRadius: '20px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
  border: `3px solid ${theme.palette.primary.main}`,
  transition: 'all 0.3s ease',
}));

const ExternalLink = styled(MuiLink)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  transition: 'color 0.3s ease',
  '&:hover': {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
  },
}));

// Timeline components
const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  marginTop: '40px',
  marginBottom: '40px',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '20px',
    top: '0',
    height: '100%',
    width: '4px',
    backgroundColor: theme.palette.mode === 'light' 
      ? 'rgba(106, 73, 219, 0.3)' 
      : 'rgba(106, 73, 219, 0.4)',
    borderRadius: '10px',
    [theme.breakpoints.up('md')]: {
      left: '50%',
      transform: 'translateX(-50%)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: '20px',
    marginBottom: '20px',
  },
}));

const TimelineItem = styled(Box)(({ theme, isLeft }) => ({
  position: 'relative',
  margin: '60px 0',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    justifyContent: isLeft ? 'flex-end' : 'flex-start',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      width: '40px',
      height: '4px',
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(106, 73, 219, 0.3)' 
        : 'rgba(106, 73, 219, 0.4)',
      right: isLeft ? 'auto' : '50%',
      left: isLeft ? '50%' : 'auto',
    }
  },
  [theme.breakpoints.down('md')]: {
    paddingLeft: '60px',
    margin: '40px 0',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '20px',
      width: '40px',
      height: '4px',
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(106, 73, 219, 0.3)' 
        : 'rgba(106, 73, 219, 0.4)',
    }
  },
  [theme.breakpoints.down('sm')]: {
    margin: '30px 0',
  },
}));

const TimelineContentWrapper = styled(Box)(({ theme, isLeft }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    width: '45%',
  },
}));

const TimelineDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  boxShadow: '0 0 0 4px rgba(106, 73, 219, 0.2)',
  zIndex: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  left: '20px',
  [theme.breakpoints.up('md')]: {
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

const TimelineContent = styled(Paper)(({ theme }) => ({
  padding: '24px',
  position: 'relative',
  width: '100%',
  borderRadius: '15px',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: '3px',
    width: '0%',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.5s ease',
    zIndex: 1,
  },
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)',
    '&::before': {
      width: '100%',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const TimelinePeriod = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.main,
  marginBottom: '10px',
  '& svg': {
    marginRight: '8px',
    fontSize: '1.2rem',
  },
}));

const TimelineTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: '5px',
  color: theme.palette.mode === 'light' ? '#333' : '#fff',
}));

const TimelineSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  marginBottom: '10px',
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: '8px',
    fontSize: '1.2rem',
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`about-tabpanel-${index}`}
      aria-labelledby={`about-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SkillCategory = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SkillCategoryTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  color: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.primary.light,
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  paddingBottom: theme.spacing(1),
  display: 'inline-block',
}));

const SkillsGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SkillItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));

const SkillProgressWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const SkillProgress = styled(LinearProgress)(({ theme }) => ({
  width: '100%',
  height: 8,
  borderRadius: 5,
  backgroundColor: theme.palette.mode === 'light' 
    ? 'rgba(0, 0, 0, 0.1)' 
    : 'rgba(255, 255, 255, 0.1)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
  },
}));

const SkillIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
}));

const SkillIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'light' 
    ? 'rgba(106, 73, 219, 0.1)' 
    : 'rgba(106, 73, 219, 0.2)',
  color: theme.palette.primary.main,
  '& svg': {
    fontSize: 22,
  },
}));

const About = () => {
  const [value, setValue] = useState(0);
  const theme = useMuiTheme();

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const skills = [
    { 
      category: 'Mobile App Dev', 
      items: ['Flutter', 'Dart', 'Google Firestore'],
      icon: <CodeIcon sx={{ color: 'primary.main' }} />
    },
    { 
      category: 'Hardware Programming', 
      items: ['Arduino', 'RasperryPi', 'ARM Cortex M0'],
      icon: <MemoryIcon sx={{ color: 'primary.main' }} />
    },
    { 
      category: 'Programming languages', 
      items: ['Python', 'C', 'C++', 'Java', 'HTML', 'CSS', 'Verilog'],
      icon: <TerminalIcon sx={{ color: 'primary.main' }} />
    },
    { 
      category: 'Machine Learning Frameworks', 
      items: ['Scikit-learn', 'PyTorch', 'NumPy', 'Matplotlib', 'Seaborn'],
      icon: <DataObjectIcon sx={{ color: 'primary.main' }} />
    }
  ];

  const experiences = [
    {
      role: 'Computer Vision & Machine Learning Intern',
      company: 'Electric.Friends',
      period: 'Jan 2025 - Present',
      link: 'https://electric.friends/',
      description: 'Developed and fine-tuned YOLOv8 models for real-time handball detection, achieving 65% Precision. Created and annotated a custom dataset of 5,000+ frames, implementing ByteTrack for object tracking with 75% Recall and 60% IoU. Spearheaded rapid integration of YOLOv12-n (2.6M params) within 4 days of release, capitalizing on its efficient architecture for real-time detection.'
    },
    {
      role: 'Research Assistant',
      company: 'National University of Singapore (NUS)',
      period: 'Aug 2024 - Dec 2024',
      link: 'https://www.nus.edu.sg/',
      description: 'Conducted research on advanced computer vision algorithms and embedded systems optimization for autonomous applications.'
    },
    {
      role: 'Teaching Assistant',
      company: 'National University of Singapore (NUS)',
      period: 'Aug 2024 - Nov 2024',
      link: 'https://www.nus.edu.sg/',
      description: 'Assisted in teaching computer engineering modules, conducted tutorials and lab sessions, and graded assignments for undergraduate students.'
    },
    {
      role: 'Software Engineering Intern',
      company: 'H3Dynamics',
      period: 'May 2024 - Aug 2024',
      link: 'https://www.h3dynamics.com/',
      description: 'Working on developing software solutions for autonomous drone systems. Implementing computer vision algorithms and backend services.'
    },
    {
      role: 'Teaching Assistant',
      company: 'National University of Singapore (NUS)',
      period: 'Jan 2024 - May 2024',
      link: 'https://www.nus.edu.sg/',
      description: 'Assisted in teaching the Computer Engineering module, conducting tutorials and lab sessions, and grading assignments.'
    },
    {
      role: 'National Service: Logistics Specialist',
      company: 'Singapore Armed Forces',
      period: 'Aug 2020 - Aug 2022',
      link: 'https://www.mindef.gov.sg/web/portal/mindef/home/',
      description: 'Managed inventory systems and logistics operations. Implemented improvements to the tracking system, increasing efficiency.'
    }
  ];

  const education = [
    {
      degree: 'Bachelor of Engineering (B.Eng. Hons)',
      institution: 'National University of Singapore (NUS)',
      period: 'Aug 2022 - Present',
      details: 'First Class Honours: GPA - 4.5/5.0',
      link: 'https://www.nus.edu.sg/',
      description: 'Pursuing Computer Engineering with focus on entrepreneurship and specialization in IoT.'
    },
    {
      degree: 'Higher secondary education',
      institution: 'Chettinad HariShree Vidyalayam',
      period: 'Jun 2018 - May 2020',
      link: 'https://www.harishree.org/',
      description: 'Graduated with distinction in Mathematics, Physics, and Computer Science.'
    },
    {
      degree: 'Online Courses',
      institution: 'Various Platforms',
      period: '2020 - Present',
      description: 'Completed courses in Data Science, Programming, and Machine Learning from platforms like edX, Udemy, and freeCodeCamp.',
      courses: [
        {
          name: 'Data Analysis with Python',
          platform: 'freeCodeCamp',
          link: 'https://www.freecodecamp.org/learn/data-analysis-with-python/#data-analysis-with-python-course'
        },
        {
          name: 'CS50x',
          platform: 'edx',
          link: 'https://cs50.harvard.edu/x/2024/'
        },
        {
          name: 'CS50P',
          platform: 'edx',
          link: 'https://cs50.harvard.edu/python/2022/'
        },
        {
          name: 'OpenCV Bootcamp',
          platform: 'OpenCV University',
          link: 'https://opencv.org/university/free-opencv-course/?utm_source=opcvu&utm_medium=menu&utm_campaign=obc'
        },
        {
          name: 'Python for Data Science and Machine Learning',
          platform: 'Udemy',
          link: 'https://www.udemy.com/course/python-for-data-science-and-machine-learning-bootcamp/?couponCode=24T6MT62024'
        },
        {
          name: 'Pytorch for Deep Learning',
          platform: 'ZerotoMastery',
          link: 'https://www.learnpytorch.io/'
        }
      ]
    }
  ];

  const technicalSkills = [
    { 
      name: 'Flutter',
      icon: <SiFlutter />,
      proficiency: 90
    },
    { 
      name: 'React',
      icon: <SiReact />,
      proficiency: 65
    },
    { 
      name: 'Python',
      icon: <SiPython />, 
      proficiency: 82
    },
    { 
      name: 'C/C++',
      icon: <SiCplusplus />,
      proficiency: 80
    },
    { 
      name: 'Java',
      icon: <FaJava />,
      proficiency: 70
    },
    { 
      name: 'Dart',
      icon: <SiDart />,
      proficiency: 85
    }
  ];

  const otherSkills = [
    { 
      name: 'Arduino',
      icon: <SiArduino />, 
      proficiency: 85
    },
    { 
      name: 'Raspberry Pi',
      icon: <SiRaspberrypi />,
      proficiency: 78
    },
    { 
      name: 'Embedded Systems',
      icon: <FaMicrochip />,
      proficiency: 75
    },
    { 
      name: 'Robotics',
      icon: <FaRobot />,
      proficiency: 72
    },
    { 
      name: 'Machine Learning',
      icon: <SiTensorflow />,
      proficiency: 65
    },
    { 
      name: 'Firebase',
      icon: <SiFirebase />,
      proficiency: 80
    }
  ];

  return (
    <Box 
      id="about" 
      sx={{ 
        py: { xs: 8, md: 12 }, 
        backgroundColor: 'background.paper',
      }}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              mb: 3,
              color: 'primary.main',
              fontWeight: 700,
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            About Me
          </Typography>
          
          <StyledTabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            textColor="inherit"
            allowScrollButtonsMobile
            sx={{ 
              justifyContent: 'center', 
              maxWidth: '100%',
              '.MuiTabs-scrollButtons': {
                color: 'primary.main',
              }
            }}
          >
            <StyledTab icon={<SchoolIcon />} iconPosition="start" label="Education" />
            <StyledTab icon={<WorkIcon />} iconPosition="start" label="Experience" />
            <StyledTab icon={<CodeIcon />} iconPosition="start" label="Skills" />
          </StyledTabs>

          <TabPanel value={value} index={0}>
            <TimelineContainer>
              {education.map((item, index) => (
                <TimelineItem 
                  key={index}
                  isLeft={index % 2 === 0}
                >
                  <TimelineDot />
                  <TimelineContentWrapper isLeft={index % 2 === 0}>
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <TimelineContent>
                        <TimelinePeriod>
                          <CalendarTodayIcon />
                          <Typography variant="subtitle2">{item.period}</Typography>
                        </TimelinePeriod>
                        <TimelineTitle variant="h5">{item.degree}</TimelineTitle>
                        <TimelineSubtitle variant="subtitle1">
                          <BusinessIcon />
                          <ExternalLink href={item.link} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                            {item.institution}
                          </ExternalLink>
                        </TimelineSubtitle>
                        {item.details && (
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'primary.main',
                              mb: 1
                            }}
                          >
                            {item.details}
                          </Typography>
                        )}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.7
                          }}
                        >
                          {item.description}
                        </Typography>
                        {item.courses && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                              Relevant Courses:
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: 1,
                              mt: 1
                            }}>
                              {item.courses.slice(0, 3).map((course, idx) => (
                                <ExternalLink 
                                  key={idx} 
                                  href={course.link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(106, 73, 219, 0.1)',
                                    padding: '4px 10px',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    minHeight: '28px',
                                    textAlign: 'center',
                                    '&:hover': {
                                      backgroundColor: 'rgba(106, 73, 219, 0.2)',
                                      textDecoration: 'none',
                                    }
                                  }}
                                >
                                  {course.name}
                                </ExternalLink>
                              ))}
                              {item.courses.length > 3 && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  +{item.courses.length - 3} more
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </TimelineContent>
                    </motion.div>
                  </TimelineContentWrapper>
                </TimelineItem>
              ))}
            </TimelineContainer>
          </TabPanel>

          <TabPanel value={value} index={1}>
            <TimelineContainer>
              {experiences.map((item, index) => (
                <TimelineItem 
                  key={index}
                  isLeft={index % 2 === 0}
                >
                  <TimelineDot />
                  <TimelineContentWrapper isLeft={index % 2 === 0}>
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <TimelineContent>
                        <TimelinePeriod>
                          <CalendarTodayIcon />
                          <Typography variant="subtitle2">{item.period}</Typography>
                        </TimelinePeriod>
                        <TimelineTitle variant="h5">{item.role}</TimelineTitle>
                        <TimelineSubtitle variant="subtitle1">
                          <BusinessIcon />
                          <ExternalLink href={item.link} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                            {item.company}
                          </ExternalLink>
                        </TimelineSubtitle>
                        <Typography 
                          variant="body2"
                          sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.7
                          }}
                        >
                          {item.description}
                        </Typography>
                      </TimelineContent>
                    </motion.div>
                  </TimelineContentWrapper>
                </TimelineItem>
              ))}
            </TimelineContainer>
          </TabPanel>

          <TabPanel value={value} index={2}>
            <Box sx={{ mt: 4 }}>
              <SkillCategory>
                <SkillCategoryTitle variant="h5">
                  Technical Skills
                </SkillCategoryTitle>
                <SkillsGrid container spacing={4}>
                  {technicalSkills.map((skill, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <SkillItem>
                          <SkillIconWrapper>
                            <SkillIcon>
                              {skill.icon}
                            </SkillIcon>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {skill.name}
                            </Typography>
                          </SkillIconWrapper>
                          <SkillProgressWrapper>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                              Proficiency
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="primary">
                              {skill.proficiency}%
                            </Typography>
                          </SkillProgressWrapper>
                          <SkillProgress 
                            variant="determinate" 
                            value={skill.proficiency} 
                          />
                        </SkillItem>
                      </motion.div>
                    </Grid>
                  ))}
                </SkillsGrid>
              </SkillCategory>

              <SkillCategory>
                <SkillCategoryTitle variant="h5">
                  Other Skills
                </SkillCategoryTitle>
                <SkillsGrid container spacing={4}>
                  {otherSkills.map((skill, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <SkillItem>
                          <SkillIconWrapper>
                            <SkillIcon>
                              {skill.icon}
                            </SkillIcon>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {skill.name}
                            </Typography>
                          </SkillIconWrapper>
                          <SkillProgressWrapper>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                              Proficiency
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="primary">
                              {skill.proficiency}%
                            </Typography>
                          </SkillProgressWrapper>
                          <SkillProgress 
                            variant="determinate" 
                            value={skill.proficiency} 
                          />
                        </SkillItem>
                      </motion.div>
                    </Grid>
                  ))}
                </SkillsGrid>
              </SkillCategory>
            </Box>
          </TabPanel>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About; 